import './_privacySettings.scss'
import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import FixedBottomButton from 'components/UI/FixedBottomButton/FixedBottomButton'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import SMSVerificationCode from 'features/VerificationCode/SmsVerificationCode'
import {
    getQRCode,
    getSMSVerificationCode,
    setQRCode
    // eslint-disable-next-line import/no-unresolved
} from 'features/VerificationCode/Verification'
import VerificationCode from 'features/VerificationCode/VerificationCode'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { SuccessfulChange } from 'pages/settings/features/SuccessfulChange'
import { useUserProfile } from 'store'

export const AuthentificatorApp = () => {
    const navigate = useNavigate()
    const userData = useUserProfile()
    const { t } = useTranslation()
    const hasFetchedRef = useRef(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [switchBtn, setSwitchBtn] = useState<{
        name: string
        value: boolean
    }>({ name: '', value: false })
    const [CodeFilled, setCodeFilled] = useState<boolean>(false)
    const [isAppAuthentificator, setIsAppAuthentificator] =
        useState<boolean>(false)
    const [inputValue, setInputValue] = useState<string>('')
    const [otpResponse, setOtpResponse] = useState<any>(null)
    const [qrSvg, setQrSvg] = useState<string | null>(null)
    const [secrateCode, setSecrateCode] = useState('')
    const [alreadyVerified, setAlreadyVerified] = useState(false)
    const [message, setMessage] = useState(null)
    const [toggleActive, setToggleActive] = useState(false)
    const isQrAuthentificator =
        userData?.two_factor_auth_type === 'qr_authenticator'
    const isSMSAuthentificator = userData?.two_factor_auth_type === 'sms'
   
    useEffect(() => {
        if (!userData?.two_factor_auth_type) return;
    
        if (
            (isAppAuthentificator && userData.two_factor_auth_type === 'qr_authenticator') ||
            (!isAppAuthentificator && userData.two_factor_auth_type === 'sms')
        ) {
            setAlreadyVerified(true);
        } else {
            setAlreadyVerified(false);
        }
    }, [isAppAuthentificator, userData?.two_factor_auth_type]);

    useEffect(() => {
        if (window.location.pathname.includes('authentificator-app')) {
            setIsAppAuthentificator(true)
        }
    }, [])

    useEffect(() => {
        if (isQrAuthentificator) {
            setSwitchBtn({ name: 'authenticatorApp', value: true })
        } else if (isSMSAuthentificator) {
            setSwitchBtn({ name: 'smsVerification', value: true })
        } else {
            setSwitchBtn({ name: '', value: false })
        }
    }, [isQrAuthentificator, isSMSAuthentificator])

    const SubmitOtp = async () => {
        if (otpResponse === null && !inputValue) {
            return
        }

        try {
            const response = await getSMSVerificationCode({
                otp: inputValue,
                reference: otpResponse
            })
            if (!response?.success) {
                addToast(
                    'error',
                    t(response.message || 'OTP verification failed')
                )
                return
            }
            addToast('success', t('OTP verified successfully'))
            submitPage()
        } catch (error) {
            const message =
                (error as any)?.response?.data?.message ||
                t('An unexpected error occurred')
            addToast('error', t(message))
        }
    }

    const SubmitQROtp = async () => {
        if (secrateCode === '' && !inputValue) {
            return
        }

        try {
            const response = await setQRCode(inputValue)
            if (response?.status !== 'success') {
                addToast(
                    'error',
                    t(response.message || 'OTP verification failed')
                )
                return
            }
            addToast('success', t('OTP verified successfully'))
            submitPage()
        } catch (error) {
            const message =
                (error as any)?.response?.data?.message ||
                t('An unexpected error occurred')
            addToast('error', t(message))
        }
    }

    useEffect(() => {
        if (
            !isAppAuthentificator &&
            window.location.pathname.includes('/sms-verification')
        )
            return
        if (hasFetchedRef.current) return
        hasFetchedRef.current = true

        getQRCode().then(response => {
            if (response?.status === 'success') {
                setQrSvg(response.qr_svg)
                setSecrateCode(response.secret)
                setMessage(response.message)
            }
        })
    }, [isAppAuthentificator])

    const renderCurrentPage = () => {
        switch (currentPage) {
            case 1:
                return (
                    <>
                        {alreadyVerified ? (
                            <>
                                <SuccessfulChange
                                    img={Icons.scan}
                                        header={t('enabled')}

                                    text={t(
                                        'twoFactorAuthenticationCurrentlyEnabled'
                                    )}
                                />
                                <div>
                                    <Button
                                        text={t('Reset')}
                                        color='black'
                                        font='mont-14-normal'
                                        width='13'
                                        height='3'
                                        disabled={false}
                                        clickFn={() => {
                                            setAlreadyVerified(false)
                                        }}
                                        customClass='verification-btn'
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                {isAppAuthentificator && (
                                    <>
                                        <div className='authenticatorApp'>
                                            <h3>{t('howToSetup')}</h3>
                                            <p>
                                                {t(
                                                    'downloadInstallMicrosoftOrGoogleAuthenticator'
                                                )}{' '}
                                                <img
                                                    src={Icons.googlePlay}
                                                    alt='Google Play'
                                                />
                                            </p>
                                            <p>
                                                {t('inAuthenticatorAppOpen')}
                                                <img
                                                    src={Icons.appStore}
                                                    alt={t('appStore')}
                                                />
                                            </p>
                                        </div>
                                        <QRBox>
                                                {qrSvg?.includes('<svg') ? (
                                                    <>
                                                        <div
                                                            className='qr-container'
                                                            dangerouslySetInnerHTML={{
                                                                __html: qrSvg
                                                            }}
                                                        />
                                                        <TextBox>
                                                            <Text>
                                                                Or enter this code into your authentificator app
                                                            </Text>
                                                            <Code>{secrateCode}</Code>
                                                        </TextBox>
                                                    </>
                                                ) : (
                                                    <p>Loading QR code...</p>
                                                )}
                                        </QRBox>
                                    </>
                                )}

                                {/* <VerificationCode/> */}
                                <SMSVerificationCode
                                    isAppAuthentificator={isAppAuthentificator}
                                    setCodeFilled={setCodeFilled}
                                    setInputValue={setInputValue}
                                    setOtpResponse={setOtpResponse}
                                />
                                <Button
                                    text={t('submit')}
                                    color='black'
                                    font='mont-14-normal'
                                    width='13'
                                    height='3'
                                    disabled={!CodeFilled}
                                    clickFn={() => {
                                        if (isAppAuthentificator) {
                                            SubmitQROtp()
                                        } else SubmitOtp()
                                    }}
                                    customClass='verification-btn'
                                />
                            </>
                        )}
                    </>
                )

            case 2:
                return (
                    <>
                        <SuccessfulChange
                            img={Icons.scan}
                            text={t('twoFactorAuthenticationHasBeenVerified')}
                        />
                        <FixedBottomButton
                            text={t('close')}
                            customClass='accountsettings__card__fixedbottombtn'
                            clickFn={() => {
                                navigate(
                                    '/settings/account?privacy-and-security'
                                )
                            }}
                        />
                    </>
                )
        }
    }

    const submitPage = () => {
        if (currentPage === 1) {
            setCurrentPage(2)
        }
    }

    const toggleSwitch = () => {
        setSwitchBtn({
            value: !switchBtn.value,
            name: isAppAuthentificator ? 'authenticatorApp' : 'smsVerification'
        })
    }

    useEffect(() => {
        if (!userData?.two_factor_auth_type) return;
    
        if (
            (isAppAuthentificator && userData.two_factor_auth_type === 'qr_authenticator') ||
            (!isAppAuthentificator && userData.two_factor_auth_type === 'sms')
        ) {
            setToggleActive(true);
        } else {
            setToggleActive(false);
        }
    }, [isAppAuthentificator, userData?.two_factor_auth_type]);
    
    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='switch'
                    section={t('twoStepAuthentication')}
                    title={
                        isAppAuthentificator
                            ? t('authenticatorApp')
                            : t('smsVerification')
                    }
                    switchActive={toggleActive}
                    switchFn={() => {
                        toggleSwitch()
                    }}
                />
            }
        >
            {renderCurrentPage()}
        </WithHeaderSection>
    )
}

const QRBox = styled.div`
    width: 100%;
    background: linear-gradient(89.85deg, #fbfbfb 0.1%, #ffffff 102.02%);
    box-shadow: 0 0.4rem 2rem rgba(0, 0, 0, 0.05);
    border-radius: 2rem;
    display: flex;
    // flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
    margin: 2.5rem 0rem;
    padding: 0rem 1rem;
    min-height: 15rem;
    transition: all ease-in-out 0.3s;
    border: 0.2rem solid transparent;
    position: relative;
    font-family: 'Montserrat', sans-serif;
    font-style: normal;

    .qr-container svg {
        width: 120px !important;
        height: 120px !important;
    }
`
const Code = styled.div`
    margin-top: 1rem;
    font-weight: 600;
    font-size: 1.4rem;
    letter-spacing: 0.2rem;
    color: #000;
`
const TextBox = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    justify-content: center;
    padding: 1rem 0;
`

const Text = styled.div`
    font-weight: 500;
    font-size: 1.3rem;
    color: #afafaf;
    margin-top: 1rem;
`
